import { Component, EventEmitter, Input, OnInit, Output, ChangeDetectionStrategy } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { FilterResponse } from 'src/app/shared/models/filter-response';
import BaseComponent from '../../../base.component';
import { debounceTime } from 'rxjs';

@Component({
    selector: 'cdp-date-picker',
    templateUrl: './date-picker.component.html',
    styleUrls: ['date-picker.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class DatePickerComponent extends BaseComponent implements OnInit {
    @Input() tooltip?: string;
    @Input() controlName: string;

    private _minDateSelection: string | Date;
    get minDateSelection(): string | Date {
        return this._minDateSelection;
    }
    @Input()
    set minDateSelection(value: string | Date) {
        if (value !== this._minDateSelection) {
            this._minDateSelection = value;
            this.selectedMinDateControl.setValue(value);
        }
    }

    private _maxDateSelection: string | Date;
    get maxDateSelection(): string | Date {
        return this._maxDateSelection;
    }
    @Input()
    set maxDateSelection(value: string | Date) {
        if (value !== this._maxDateSelection) {
            this._maxDateSelection = value;
            this.selectedMaxDateControl.setValue(value);
        }
    }

    @Output() minDateSelectionChange: EventEmitter<FilterResponse<string>> = new EventEmitter<FilterResponse<string>>();
    @Output() maxDateSelectionChange: EventEmitter<FilterResponse<string>> = new EventEmitter<FilterResponse<string>>();

    readonly selectedMinDateControl: FormControl = new FormControl('');
    readonly selectedMaxDateControl: FormControl = new FormControl('');
    readonly minMaxDateSelectGroup = new FormGroup({
        selectedMinDateControl: this.selectedMinDateControl,
        selectedMaxDateControl: this.selectedMaxDateControl
    });

    constructor() {
        super();
    }

    ngOnInit(): void {
        this.minMaxDateSelectGroup.valueChanges.pipe(this.unsubscribeOnDestroy()).subscribe(selection => {
            if (
                this.minDateSelection !== selection.selectedMinDateControl &&
                !(!this.minDateSelection && !selection.selectedMinDateControl)
            ) {
                const minResponse: FilterResponse<string> = {
                    name: `${this.controlName}Min`,
                    selection: selection.selectedMinDateControl
                };
                this.minDateSelectionChange.emit(minResponse);
            }

            if (
                this.maxDateSelection !== selection.selectedMaxDateControl &&
                !(!this.maxDateSelection && !selection.selectedMaxDateControl)
            ) {
                const maxResponse: FilterResponse<string> = {
                    name: `${this.controlName}Max`,
                    selection: selection.selectedMaxDateControl
                };
                this.maxDateSelectionChange.emit(maxResponse);
            }
        });

        this.minMaxDateSelectGroup.valueChanges.pipe(debounceTime(1000), this.unsubscribeOnDestroy()).subscribe(() => {
            this.adjustMaxDateRangeFilter();
        });
    }

    adjustMaxDateRangeFilter() {
        if (!this.selectedMinDateControl.value || !this.selectedMaxDateControl.value) {
            return;
        }

        const minDate: number = new Date(this.selectedMinDateControl.value).getDate();
        const maxDate: number = new Date(this.selectedMaxDateControl.value).getDate();

        if (maxDate < minDate) {
            const adjustedDate: Date = new Date(this.selectedMaxDateControl.value.setDate(minDate + 1));
            this.selectedMaxDateControl.setValue(adjustedDate);
        }
    }

    get minDate(): string {
        const date: any = this.selectedMinDateControl.value;

        if (!date) {
            return '';
        } else if (typeof date === 'string') {
            if (date.length) {
                return new Date(date).toISOString();
            } else {
                return '';
            }
        } else {
            return date?.toISOString();
        }
    }

    get maxDate(): string {
        const date: any = this.selectedMaxDateControl.value;

        if (!date) {
            return '';
        } else if (typeof date === 'string') {
            if (date.length) {
                return new Date(date).toISOString();
            } else {
                return '';
            }
        } else {
            return date?.toISOString();
        }
    }
}
